import { ValidatorFn, AbstractControl } from '@angular/forms';

/**
 * Validador para números de documento según su tipo (DNI, CUIT, CUIL, PASAPORTE)
 * Aplica reglas específicas según el tipo de documento seleccionado
 */
export function documentNumberValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control || !control.value) {
      return null;
    }

    const tipoDocumento = control.parent?.get('tipoDocumento')?.value || 'DNI';
    let documentNumber = String(control.value);
    let cleanedValue = '';
    let maxLength = 0;

    switch (tipoDocumento) {
      case 'DNI':
        maxLength = 9;
        break;
      case 'CUIT':
      case 'CUIL':
        maxLength = 11;
        break;
      case 'PASAPORTE':
        maxLength = 9;
        break;
      default:
        maxLength = 8;
    }

    cleanedValue = documentNumber.replace(/[^\d]/g, '');

    if (cleanedValue.length > maxLength) {
      cleanedValue = cleanedValue.substring(0, maxLength);
    }

    if (cleanedValue !== documentNumber) {
      control.setValue(cleanedValue, { emitEvent: false });
    }

    switch (tipoDocumento) {
      case 'DNI':
        return (cleanedValue.length >= 7 && cleanedValue.length <= 9)
          ? null
          : { invalidDocumentNumber: true };
      case 'CUIT':
      case 'CUIL':
        return cleanedValue.length === 11 ? null : { invalidDocumentNumber: true };
      case 'PASAPORTE':
        return (cleanedValue.length >= 6 && cleanedValue.length <= 9) ? null : { invalidDocumentNumber: true };
      default:
        return null;
    }
  };
}

/**
 * Validador para nombre y apellido (solo letras, espacios y apóstrofes)
 * Limpia automáticamente caracteres no permitidos
 */
export function onlyLettersValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control || !control.value) {
      return null;
    }

    const value = String(control.value);

    const invalidChars = /[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s']/.test(value);

    if (invalidChars) {
      const cleanedValue = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s']/g, '');

      control.setValue(cleanedValue, { emitEvent: false });

      return null;
    }

    return null;
  };
}

/**
 * Validador para números de teléfono y código de área
 * - Para números de teléfono: verifica longitud mínima y que no todos los dígitos sean iguales
 * - Para código de área: simplemente permite solo dígitos
 * - Ambos campos automáticamente limpian caracteres no numéricos
 */
export function phoneNumberValidator(minLength: number = 6): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control || !control.value) {
      return null;
    }

    const value = String(control.value);
    const controlName = control.parent?.controls ?
      Object.keys(control.parent.controls).find(key => control.parent?.get(key) === control) : '';
    const isAreaCode = controlName === 'codigoArea';

    const digitsOnly = value.replace(/[^\d]/g, '');

    if (digitsOnly !== value) {
      control.setValue(digitsOnly, { emitEvent: false });
    }

    if (isAreaCode) {
      return null;
    }

    if (digitsOnly.length < minLength) {
      return { minlength: { requiredLength: minLength, actualLength: digitsOnly.length } };
    }

    if (/^(\d)\1+$/.test(digitsOnly)) {
      return { allSameDigits: true };
    }

    return null;
  };

}

/**
 * Validador para fechas que no deben ser anteriores a la fecha actual
 * Valida tanto fechas ingresadas manualmente como seleccionadas por date picker
 */
export function dateNotInPastValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control || !control.value) {
      return null; // Si no hay valor, no validar (Validators.required se encarga de eso)
    }

    // Obtener la fecha actual en formato YYYY-MM-DD (mismo formato que el input date)
    const today = new Date();
    const todayString = today.getFullYear() + '-' +
      String(today.getMonth() + 1).padStart(2, '0') + '-' +
      String(today.getDate()).padStart(2, '0');

    // El valor del input date ya viene en formato YYYY-MM-DD
    const inputDateString = control.value;

    // Comparar directamente las strings de fecha
    if (inputDateString < todayString) {
      return { dateInPast: true };
    }

    return null;
  };
}